import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { EmptyState, PageHeader, TableSkeleton } from "../components/ui";
import { formatDateTime } from "../lib/format";

/** The superadmin's own ledger — who touched which account, and when. */
export function PlatformAuditPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;

  const audit = useQuery({
    queryKey: ["platform-audit"],
    queryFn: ({ signal }) => api.platformAudit(signal),
  });

  const rows = audit.data?.results ?? [];

  return (
    <>
      <PageHeader
        title="Audit trail"
        subtitle="Operator actions across the book. Read-only, and only for platform staff."
      />

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {audit.isPending ? (
          <TableSkeleton rows={8} />
        ) : audit.isError ? (
          <EmptyState title="Could not load the audit trail" body="Please try again." />
        ) : rows.length === 0 ? (
          <EmptyState title="Nothing recorded yet" body="Operator actions will appear here as they happen." />
        ) : (
          <ul className="divide-y divide-slate-100">
            {rows.map((entry) => (
              <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{entry.action}</p>
                  <p className="text-xs text-slate-500">
                    {entry.account_name || "No account"} · {entry.operator_email || "System"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs tabular-nums text-slate-500">{formatDateTime(entry.created_at, timezone)}</p>
                  <p className="max-w-64 truncate text-xs text-slate-400">{detailText(entry.detail)}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

function detailText(detail: unknown): string {
  if (!detail) return "";
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
  } catch {
    return "—";
  }
}